import './Dashboard.css'

import React, { useMemo, useRef } from 'react'
import { useState, useEffect } from 'react'
import { BackButton, NavBar } from '@/components/layout/NavBar'
import { Services } from '@/services/services'

import { CurrentSession } from '@/services/repository/session'
import { MainPageLayout } from '@/components/layout/MainLayout'
import { useTranslations } from '@/hooks/useTranslations'
import { Roles, User } from '@/services/repository/users'
import { UserDashboardList } from './components/List'
import { DatePeriod, datePeriodToQueryParams, DateTimePeriodBody, decodeDatePeriod, StretchOfTime } from '@/popups/dateTimePeriod'
import { useModal } from '@/components/modal/Modal'
import { formatDate } from '@/common/time'
import { StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { CommunalUserPathMap } from '@/pages/communal/statistics/userstatistic/components/Map/PathMap'
import { ActivityStatus, FailureResponse, HitsData, SessionsResponse, SessionStat } from '../components/common/data'
import { ActivityStatusBox } from '@/pages/communal/statistics/components/statistic/ActivityStatusBox'
import { ActivityTextBox } from '@/pages/communal/statistics/components/statistic/ActivityTextBox'
import { Spinner } from '@/components/spinner'
import { SessionComparableFns, SessionDefaultOrder, SessionList, SessionListOrderCols } from '@/pages/communal/statistics/sessions/SessionList'
import { FailuresTextBox } from '@/pages/communal/statistics/components/statistic/FailuresTextBox'
import { HitData } from '@/services/model/machine'
import { sendHistoriesRequest, UserDeviceHistoriesData, UserDeviceHistoriesMachine } from '../requests/histories'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { updateSearchParams } from '@/common/reactnav'
import { SessionPopBody } from '../sessions/SessionPop'
import { Order, OrderManager } from '@/components/order/Order'

export const UsersStatistics = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const translates = useTranslations(services, session)
  const navigate = useNavigate()
  const [user, setUser] = useState<User>()
  const [users, setUsers] = useState<User[]>()
  // const [statistic, setStatistic] = useState<ActivityStatus | undefined>(undefined)
  // const [timeline, setTimeline] = useState<any>(undefined)
  const [selectedMachine, setSelectedMachine] = useState<UserDeviceHistoriesMachine | undefined>(undefined)
  const [sessions, setSessions] = useState<SessionsResponse>()
  const [failures, setFailures] = useState<FailureResponse>()
  const [historiesData, setHistoriesData] = useState<UserDeviceHistoriesData | null | undefined>()
  // const [filtredHistoriesData, setFiltredHistoriesData] = useState<UserDeviceHistoriesData | null | undefined>()
  const [sessionPagginationState, setSessionPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 20
  })
  const [sessionOrderManager, setSessionOrderManager] = useState<OrderManager>(new OrderManager(SessionListOrderCols, SessionDefaultOrder, (order: Order) => {
    setSessionOrderManager(sessionOrderManager.newManager(order))
    setSessionPagginationState({
      ...sessionPagginationState,
      pageNumber: 1
    })
  }, SessionComparableFns))
  const [searchParams] = useSearchParams();
  const inUser = searchParams.get("user");

  const [criteria, setCriteria] = useState<SearchCriteria>({
    userId: undefined,
    period: decodeDatePeriod(searchParams)
  })
  const [listState, setListState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 5
  })
  const { openModal, refreshModals } = useModal()

  function goToUserPage(userId: string) {
    navigate(`/communal/statistics/users?user=${userId}&for=${criteria.period?.for}&length=${criteria.period?.length}`)
  }

  function goToMachinePage(machineId: string) {
    navigate(`/communal/statistics/machines?machine=${machineId}&for=${criteria.period?.for}&length=${criteria.period?.length}`)
  }

  const goToUserFailures = () => {
    // window.open(`/communal/user/${user?.id}/failures`, '_blank')
    // It is not required - it is good idea to add something like that in the future.
    // TODO
  }

  const openDetails = (row: SessionStat) => {
    if (!historiesData) {
      return
    }
    openModal((props) => SessionPopBody({ ...props, session, services, row, user, machine: undefined, historiesData }))
  }


  const openMaxHit = () => {
    const maxHitSession = sessions?.reduce((maxSession, session) =>
      Math.max(session.maxHitX, session.maxHitY, session.maxHitZ) > Math.max(maxSession.maxHitX, maxSession.maxHitY, maxSession.maxHitZ) ? session : maxSession
    );
    maxHitSession && openDetails(maxHitSession)
  }

  const openAvgList = () => {
    if (!historiesData || !sessions || !user) {
      return
    }
    openModal((props) => {
      const filtredSessions = sessions?.filter(s => s.maxHitX || s.maxHitY || s.maxHitZ)
      const [sessionPagginationState, setSessionPagginationState] = useState<StaticPaginatedTableState>({
        pageNumber: 1,
        pageSize: 8
      })
      const [sessionOrderManager, setSessionOrderManager] = useState<OrderManager>(new OrderManager(SessionListOrderCols, {
        key: 'maxHit',
        asc: false
      }, (order: Order) => {
        setSessionOrderManager(sessionOrderManager.newManager(order))
        setSessionPagginationState({
          ...sessionPagginationState,
          pageNumber: 1
        })
      }, SessionComparableFns))

      useEffect(refreshModals, [sessionPagginationState, sessionOrderManager])

      return <div className='sessions-hits-popup'>
        <h2>{user.username || user.id}</h2>
        <div className='user-map dashboard-grid-section'>
          <SessionList pagginationState={sessionPagginationState} setPagginationState={setSessionPagginationState} services={services} session={session} user={user} data={filtredSessions} historiesData={historiesData} goToUserPage={goToUserPage} goToMachinePage={goToMachinePage} orderManager={sessionOrderManager} />
        </div>
      </div>
    })
  }

  useEffect(() => {
    setCriteria({
      ...criteria,
      userId: user?.id
    })
  }, [user]);

  useEffect(() => {
    updateSearchParams(location.search, navigate, {
      ...(criteria.period ? datePeriodToQueryParams(criteria.period) : {}),
      user: criteria.userId
    });
  }, [criteria]);

  useEffect(() => {
    var fetchUrl = `/api/communal/statistics/users/list`
    services.session.fetchWithToken(fetchUrl).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<any>()
      }
      const respUsers: User[] = await r.json()
      setUsers(respUsers)
      let selectedUser = respUsers?.find(row => row.id === inUser || row.username === inUser)
      if (!selectedUser && respUsers && respUsers.length > 0) {
        selectedUser = respUsers[0]
      }
      setUser(selectedUser)
      if (selectedUser) {
        const index = respUsers?.findIndex(
          row => row.id === selectedUser.id
        )
        if (index !== undefined && index !== -1) {
          const nextPageNumber = 1 + Math.floor(index / listState.pageSize);
          setListState({
            ...listState,
            pageNumber: nextPageNumber
          });
        }
      }
    })
  }, []);

  // useEffect(() => {
  //   if (!criteria.userId) {
  //     return
  //   }
  //   setStatistic(undefined)
  //   var fetchUrl = `/api/communal/user/statistic/global/statistic?${SearchCriteriaParams(criteria)}`
  //   services.session.fetchWithToken(fetchUrl).then(async (r) => {
  //     if (r.status !== 200) {
  //       const erros = await r.json()
  //       console.error(erros)
  //       return Promise.reject<any>()
  //     }
  //     setStatistic(await r.json())
  //   })
  // }, [criteria]);

  // useEffect(() => {
  //   if (!criteria.userId) {
  //     return
  //   }
  //   setTimeline(undefined)
  //   var fetchUrl = `/api/communal/user/statistic/global/timeline?${SearchCriteriaParams(criteria)}`
  //   services.session.fetchWithToken(fetchUrl).then(async (r) => {
  //     if (r.status !== 200) {
  //       const erros = await r.json()
  //       console.error(erros)
  //       return Promise.reject<any>()
  //     }
  //     setTimeline(await r.json())
  //   })
  // }, [criteria]);

  useEffect(() => {
    if (!criteria.userId) {
      return
    }
    setSessions(undefined)
    var fetchUrl = `/api/communal/statistics/sessions/list?${SearchCriteriaParams(criteria)}`
    services.session.fetchWithToken(fetchUrl).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<any>()
      }
      setSessions(await r.json())
      setSessionPagginationState({
        ...sessionPagginationState,
        pageNumber: 1
      })
    })
  }, [criteria]);

  useEffect(() => {
    if (!criteria.userId) {
      return
    }
    setFailures(undefined)
    var fetchUrl = `/api/communal/statistics/failures?${SearchCriteriaParams(criteria)}`
    services.session.fetchWithToken(fetchUrl).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<any>()
      }
      setFailures(await r.json())
    })
  }, [criteria]);

  useEffect(() => {
    if (criteria.userId === undefined) {
      setHistoriesData(null)
      return
    }
    setHistoriesData(undefined)
    sendHistoriesRequest(services, criteria).then(setHistoriesData)
  }, [criteria])

  // const filtredHistoriesData = useMemo<UserDeviceHistoriesData | undefined>(() => {
  //   if (!historiesData) {
  //     return undefined
  //   }
  //   if (!selectedMachine) {
  //     return historiesData
  //   }
  //   return {
  //     hash: Date.now().toString(),
  //     histories: historiesData ? Object.fromEntries(
  //       Object.entries(historiesData.histories)
  //         .filter(([, row]) => row.imei === selectedMachine.imei)
  //     ) : {},
  //     machines: {
  //       [selectedMachine.imei]: historiesData?.machines[selectedMachine.imei]
  //     }
  //   }
  // }, [historiesData, selectedMachine])

  const filtredSessions = useMemo<SessionsResponse | undefined>(() => {
    if (!sessions) {
      return undefined
    }
    if (!selectedMachine) {
      return sessions
    }
    return sessions.filter(row => row.imei === selectedMachine.imei)
  }, [sessions, selectedMachine])

  const statistic: ActivityStatus | undefined | null = useMemo(() => (sessions ? {
    activeTime: sessions.reduce((sum, session) => sum + (session.activeTime || 0), 0),
    onFireTime: sessions.reduce((sum, session) => sum + (session.onFireTime || 0), 0)
  } : sessions), [sessions])

  const hits: HitsData | undefined | null = useMemo(() => (sessions ? {
    totalHits: sessions.reduce((sum, session) => sum + (session.totalHits || 0), 0),
    maxHitX: sessions.reduce((max, session) => Math.max(max, Number(session.maxHitX)), 0),
    maxHitY: sessions.reduce((max, session) => Math.max(max, Number(session.maxHitY)), 0),
    maxHitZ: sessions.reduce((max, session) => Math.max(max, Number(session.maxHitZ)), 0),
    avgHit: (() => {
      const maxAcc = sessions.reduce((maxAcc, session) => maxAcc + Math.max(Number(session.avgHitX), Number(session.avgHitY), Number(session.avgHitZ)) * session.totalHits, 0)
      const totalHits = sessions.reduce((totalHits, session) => totalHits + session.totalHits, 0)
      return totalHits ? maxAcc / totalHits : 0
    })(),
  } : sessions), [sessions])

  if (translates === undefined || users === undefined) {
    return <div className='spinner'></div>
  }

  return <MainPageLayout pageName='communal-userstatistic' services={services} translates={translates} session={session} navbarLeft={<BackButton services={services} translates={session.translates} />} >

    <div className='dashboard-navigation'>
      <div className='top-icon-button' onClick={() => openModal((props) => DateTimePeriodBody({
        ...props,
        services,
        session,
        translates: session.translates,
        period: criteria.period,
        setter: (period) => {
          setCriteria({
            ...criteria,
            period
          })
        }
      }))}>
        <i className='svgfont-calendar' />
        <span>
          {session.translates.for('page.dashboard.global.for_datetime').replace('{{for_date}}', criteria.period?.for ? formatDate(criteria.period.for) : translates.for('popup.datetime_period.now')).replace('{{for_length}}', translates.for(`dicts.period_length.${criteria.period?.length}`))}
        </span>
      </div>
    </div>

    <div className="four-column-container">
      <div className="four-col dashboard-grid-section">
        <div className='users-list'>
          <UserDashboardList
            parentState={listState}
            setParentState={setListState}
            services={services}
            session={session}
            data={users}
            selected={user}
            translates={translates}
            onSelect={(selected: User | undefined) => {
              // if (selected) {
              //   navigate({
              //     pathname: location.pathname,
              //     search: `?user=${selected.username}`
              //   }, { replace: true })
              // }
              setUser(selected)
            }} />
        </div>
      </div>
      {user ? <>
        <div className="four-col dashboard-grid-section">
          {statistic ? <ActivityStatusBox services={services} session={session} activityStatus={statistic} /> : statistic === null ? translates.for('app.no_data') : <Spinner />}
        </div>
        <div className="four-col dashboard-grid-section">
          {statistic ? <ActivityTextBox services={services} session={session} activityStatus={statistic} /> : statistic === null ? translates.for('app.no_data') : <Spinner />}
        </div>
        <div className="four-col dashboard-grid-section">
          {failures ? <FailuresTextBox services={services} session={session} failures={failures} hits={hits}
            onMaxHitsClick={openMaxHit}
            onAvgHitsClick={openAvgList}
            onServicemanCounterClick={goToUserFailures}
            onUserCounterClick={goToUserFailures}
          /> : <Spinner />}
        </div>
      </> : null}
    </div>

    {user ? <h3>{user.username} </h3> : null}

    <div className='top-boxes'>
      <div className='dashboard-box-container'>
        <div className='user-map dashboard-grid-section'>
          {historiesData ? <CommunalUserPathMap services={services} session={session} historiesData={historiesData} selectedMachine={selectedMachine} setSelectedMachine={setSelectedMachine} /> : historiesData === undefined ? <Spinner /> : null}
        </div>
      </div>
    </div>

    {sessions && user ? <div className='top-boxes'>
      <div className='dashboard-box-container'>
        <div className='user-map dashboard-grid-section'>
          <SessionList pagginationState={sessionPagginationState} setPagginationState={setSessionPagginationState} services={services} session={session} user={user} data={filtredSessions} historiesData={historiesData} goToUserPage={goToUserPage} goToMachinePage={goToMachinePage} orderManager={sessionOrderManager} />
        </div>
      </div>
    </div> : null}


  </MainPageLayout>
}
